import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, AlertTriangle, Sparkles, ArrowRight } from 'lucide-react';
import { shortFood } from '../utils/recipeMatch';

const list = (foods) => foods.map(shortFood).join(', ');

// Landing banner: open matches, or invite a VIP client to fill in Food Preferences
export const RecipeMatchBanner = ({ prefs, isVip, count, onOpen }) => {
  if (!isVip || !prefs) return null;
  const hasPrefs = prefs.found && (prefs.love.length || prefs.avoid.length);
  return (
    <section className="pb-4" data-testid="recipe-match-banner">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0">
            <Sparkles size={22} className="text-terracotta" />
          </div>
          <div className="flex-1">
            <p className="font-heading text-xl text-forest">Recipes for you</p>
            <p className="font-body text-sm text-stone-500">
              {hasPrefs
                ? `${count} recipes use foods you love. Anything on your avoid list is flagged so you can swap or skip it.`
                : 'Tell Jill which foods you love and avoid, and your matching recipes will show up here.'}
            </p>
          </div>
          {hasPrefs ? (
            <button onClick={onOpen} className="inline-flex items-center justify-center gap-2 bg-forest text-cream font-body text-sm px-5 py-2.5 rounded-full hover:bg-forest-dark transition-colors" data-testid="recipe-match-open">
              Show my matches <ArrowRight size={15} />
            </button>
          ) : (
            <Link to="/quiz/food-preferences" className="inline-flex items-center justify-center gap-2 bg-terracotta text-white font-body text-sm px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity" data-testid="recipe-match-fill-prefs">
              Share my food preferences <ArrowRight size={15} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export const RecipeMatchToggle = ({ on, onToggle }) => (
  <button
    onClick={onToggle}
    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-body text-sm border transition-colors ${on ? 'bg-terracotta text-white border-terracotta' : 'bg-white text-terracotta border-terracotta/40 hover:border-terracotta'}`}
    data-testid="recipe-match-toggle"
  >
    <Heart size={14} className={on ? 'fill-current' : ''} /> For me
  </button>
);

export const RecipeMatchBadges = ({ match, testId }) => {
  if (!match || (!match.loved.length && !match.avoided.length)) return null;
  return (
    <div className="mt-2 space-y-1" data-testid={testId}>
      {match.loved.length > 0 && (
        <p className="flex items-start gap-1 font-body text-[11px] text-emerald-700 leading-snug">
          <Heart size={11} className="mt-0.5 fill-current flex-shrink-0" /> {list(match.loved)}
        </p>
      )}
      {match.avoided.length > 0 && (
        <p className="flex items-start gap-1 font-body text-[11px] text-amber-700 leading-snug">
          <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" /> Contains {list(match.avoided)}
        </p>
      )}
    </div>
  );
};

export const RecipeMatchNote = ({ match }) => {
  if (!match || (!match.loved.length && !match.avoided.length)) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="recipe-match-note">
      {match.loved.length > 0 && (
        <p className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2.5 font-body text-sm text-emerald-800">
          <Heart size={15} className="mt-0.5 fill-current flex-shrink-0" /> Uses foods you love: {list(match.loved)}
        </p>
      )}
      {match.avoided.length > 0 && (
        <p className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2.5 font-body text-sm text-amber-800">
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" /> Heads up — contains {list(match.avoided)} from your avoid list. Swap it or skip this one.
        </p>
      )}
    </div>
  );
};
